import { ApiClient } from '../api/client/apiClient'
import type { OperationResponse } from '../api/contracts/common'
import { unwrapOperationResponse } from '../api/contracts/common'
import { env } from '../config/env'
import type {
  AdministrativeAreaType,
  AreaSummary,
  LineDetail,
  LineSummary,
  NetworkSummary,
  PagedResponse,
  TransportMapLine,
} from './types'

const client = new ApiClient({ baseUrl: env.apiBaseUrl, timeoutMs: env.apiTimeoutMs })

interface PageQuery {
  search?: string
  pageIndex?: number
  pageSize?: number
}

interface MapQuery {
  administrativeAreaId?: string
  transportNetworkId?: string
}

async function get<T>(path: string, query?: Record<string, string | number | undefined>) {
  const response = await client.get<OperationResponse<T>>(path, { query })
  return unwrapOperationResponse(response)
}

async function collectPages<T>(load: (pageIndex: number) => Promise<PagedResponse<T>>) {
  const all: T[] = []
  let pageIndex = 1
  let totalPages = 1

  do {
    const page = await load(pageIndex)
    all.push(...page.items)
    totalPages = page.totalPages
    pageIndex += 1
  } while (pageIndex <= totalPages)

  return all
}

async function loadMapLines(query: MapQuery): Promise<TransportMapLine[]> {
  const summaries = await collectPages((pageIndex) =>
    get<PagedResponse<LineSummary>>('/api/website/lines', {
      pageIndex,
      pageSize: 100,
      administrativeAreaId: query.administrativeAreaId,
      transportNetworkId: query.transportNetworkId,
    }))

  const result: TransportMapLine[] = []
  for (let index = 0; index < summaries.length; index += 8) {
    const details = await Promise.all(
      summaries.slice(index, index + 8).map((line) =>
        get<LineDetail>(`/api/website/lines/${line.id}`)),
    )
    result.push(...details)
  }

  return result
}

export const transportApi = {
  areas(query: PageQuery & { type?: AdministrativeAreaType; parentId?: string } = {}) {
    return get<PagedResponse<AreaSummary>>('/api/website/areas', {
      pageIndex: query.pageIndex ?? 1,
      pageSize: query.pageSize ?? 100,
      search: query.search,
      type: query.type,
      parentId: query.parentId,
    })
  },

  networks(query: PageQuery & { primaryAreaId?: string } = {}) {
    return get<PagedResponse<NetworkSummary>>('/api/website/networks', {
      pageIndex: query.pageIndex ?? 1,
      pageSize: query.pageSize ?? 100,
      search: query.search,
      primaryAreaId: query.primaryAreaId,
    })
  },

  areaOptions() {
    return collectPages((pageIndex) => transportApi.areas({ pageIndex, pageSize: 100 }))
  },

  networkOptions(areaScopeId?: string) {
    return collectPages((pageIndex) =>
      transportApi.networks({
        pageIndex,
        pageSize: 100,
        primaryAreaId: areaScopeId,
      }))
  },

  map(query: MapQuery = {}) {
    return loadMapLines(query)
  },
}
